import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TimeControl } from "./TimeControl";

export interface AddEventPanelProps {
  onAddItem: (item: {
    title: string;
    description: string;
    duration: string;
    plannedTime: string;
    isParallel: boolean;
  }) => void;
}

export const AddEventPanel: React.FC<AddEventPanelProps> = ({ onAddItem }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("00:10:00");
  const [plannedTime, setPlannedTime] = useState("");
  const [isParallel, setIsParallel] = useState(false);

  const handleAdd = () => {
    if (!title) return;

    const durationParts = duration.split(":").map(Number);
    const durationSeconds =
      (durationParts[0] || 0) * 3600 + (durationParts[1] || 0) * 60 + (durationParts[2] || 0);
    const finalDuration = durationSeconds < 600 ? "00:10:00" : duration;

    onAddItem({
      title: title.toUpperCase(),
      description,
      duration: finalDuration,
      plannedTime,
      isParallel,
    });
    setTitle("");
    setDescription("");
    setDuration("00:10:00");
    setPlannedTime("");
    setIsParallel(false);
  };

  return (
    <div
      data-testid="rundown-add-event-panel"
      style={{
        padding: "var(--spacing-lg)",
        borderTop: "1px solid var(--color-panel-border)",
        background: "rgba(0,0,0,0.2)",
      }}
    >
      <h3
        style={{
          margin: "0 0 var(--spacing-md) 0",
          fontSize: "1rem",
          color: "var(--color-text-secondary)",
          letterSpacing: "2px",
          fontWeight: "900",
        }}
      >
        {t("controller.rundownEditor.addNewEvent")}
      </h3>
      <div
        style={{
          padding: "var(--spacing-lg)",
          background: "var(--color-bg-base)",
          border: "1px dashed var(--color-panel-border)",
          borderRadius: "var(--border-radius-md)",
          boxShadow: "var(--shadow-card)",
          display: "flex",
          gap: "var(--spacing-md)",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: 1 }}>
          <span
            style={{
              fontSize: "0.6rem",
              color: "var(--color-text-secondary)",
              fontWeight: "bold",
              display: "block",
              marginBottom: "4px",
            }}
          >
            {t("controller.rundownEditor.eventTitle")}
          </span>
          <input
            data-testid="rundown-new-item-title"
            value={title}
            onChange={(e) => setTitle(e.target.value.toUpperCase())}
            placeholder={t("controller.rundownEditor.eventTitlePlaceholder")}
            style={{
              width: "100%",
              background: "var(--color-input-bg)",
              border: "1px solid var(--color-panel-border)",
              color: "var(--color-text-primary)",
              padding: "12px var(--spacing-md)",
              borderRadius: "var(--border-radius-sm)",
              outline: "none",
              fontWeight: "bold",
            }}
          />
          <input
            data-testid="rundown-new-item-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("controller.rundownEditor.descriptionOptional")}
            style={{
              width: "100%",
              background: "var(--color-input-bg)",
              border: "1px solid var(--color-panel-border)",
              color: "var(--color-text-secondary)",
              padding: "8px var(--spacing-md)",
              borderRadius: "var(--border-radius-sm)",
              outline: "none",
              fontSize: "0.85rem",
              marginTop: "8px",
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span
            style={{ fontSize: "0.6rem", color: "var(--color-accent-warning)", fontWeight: "bold" }}
          >
            {t("controller.rundownEditor.startTime")}
          </span>
          <TimeControl value={plannedTime} onChange={setPlannedTime} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span
            style={{ fontSize: "0.6rem", color: "var(--color-text-secondary)", fontWeight: "bold" }}
          >
            {t("controller.rundownEditor.duration")}
          </span>
          <TimeControl value={duration} onChange={setDuration} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span
            style={{ fontSize: "0.6rem", color: "var(--color-text-secondary)", fontWeight: "bold" }}
          >
            {t("controller.rundownEditor.mode")}
          </span>
          <button
            data-testid="rundown-new-item-parallel-toggle"
            onClick={() => setIsParallel(!isParallel)}
            style={{
              background: isParallel ? "rgba(45, 111, 247, 0.1)" : "var(--color-input-bg)",
              border: `1px solid ${isParallel ? "var(--color-accent-primary)" : "var(--color-panel-border)"}`,
              color: isParallel ? "var(--color-accent-primary)" : "var(--color-text-muted)",
              borderRadius: "var(--border-radius-sm)",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "0.75rem",
              fontWeight: "bold",
              height: "42px",
              transition: "all 0.2s",
            }}
          >
            {isParallel
              ? t("controller.rundownEditor.parallel")
              : t("controller.rundownEditor.sequence")}
          </button>
        </div>
        <button
          data-testid="rundown-add-item-btn"
          onClick={handleAdd}
          style={{
            alignSelf: "flex-end",
            background: "var(--color-bg-base)",
            color: "var(--color-text-primary)",
            border: "1px solid var(--color-panel-border)",
            padding: "12px 24px",
            borderRadius: "var(--border-radius-sm)",
            cursor: "pointer",
            fontWeight: "bold",
            height: "42px",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--color-panel-border)";
            e.currentTarget.style.borderColor = "var(--color-text-muted)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--color-bg-base)";
            e.currentTarget.style.borderColor = "var(--color-panel-border)";
          }}
        >
          {t("controller.dashboard.addEvent")}
        </button>
      </div>
    </div>
  );
};
